import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAdminDashboard, getAdminArticles, getUsers, getCategories, getTags,
  deleteArticle, updateUserRole, createCategory, updateCategory, deleteCategory, createTag, deleteTag,
  getSiteSettings, updateSiteSettings, updateHomepageSections, updateHeaderSettings,
  updateBreakingNewsSettings, updateWeatherSettings, updateSliderSettings, updateElectionPanelSettings,
  updateCricketManualMatches,
  reorderCategories,
  resetHomepageSections, resetHeaderMenu,
  getPodcasts, createPodcast, updatePodcast, deletePodcast, uploadFile,
  getShorts, createShort, updateShort, deleteShort,
  getArticles,
  getSubscribers, removeSubscriber, toggleSubscriber,
  getMedia, deleteMediaItem, fixBrokenImages, cleanupTestData } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, AreaChart, Area } from 'recharts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import {
  LayoutDashboard, FileText, FolderOpen, Tags, Users, Plus, Trash2, Edit3, Eye, 
  TrendingUp, MessageSquare, Mail, ChevronRight, Search, Bell, Settings, 
  Shield, Crown, PenTool, BookOpen, MoreVertical, ExternalLink, Calendar,
  ArrowUpRight, ArrowDownRight, Activity, Zap, Filter, Download, Image, Clock,
  GripVertical, ToggleLeft, ToggleRight, Globe, CloudSun, Megaphone, Menu, 
  Link2, Type, Gauge, Save, Check, X, ChevronUp, ChevronDown,
  Headphones, Video, Upload, Loader2, Play, Music, Vote
} from 'lucide-react';

const COLORS = ['#D4AF37', '#DC143C', '#1A4D2E', '#9333EA', '#0369A1', '#B91C1C', '#0F766E'];

// Role hierarchy and permissions
const ROLE_HIERARCHY = {
  admin: { level: 4, label: 'Administrator', icon: Crown, color: 'from-amber-500 to-orange-600', permissions: ['all'] },
  editor: { level: 3, label: 'Editor', icon: PenTool, color: 'from-purple-500 to-indigo-600', permissions: ['edit_all_articles', 'publish', 'manage_categories'] },
  author: { level: 2, label: 'Author', icon: BookOpen, color: 'from-blue-500 to-cyan-600', permissions: ['create_articles', 'edit_own'] },
  reader: { level: 1, label: 'Reader', icon: Eye, color: 'from-slate-400 to-slate-600', permissions: ['read'] }
};

// --- Inline-editable Category card ---
function CategoryCard({ cat, articleCount, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: cat.name || '', name_kn: cat.name_kn || '', color: cat.color || '#D4AF37' });

  useEffect(() => { setForm({ name: cat.name || '', name_kn: cat.name_kn || '', color: cat.color || '#D4AF37' }); }, [cat.name, cat.name_kn, cat.color]);

  const save = async () => {
    if (!form.name && !form.name_kn) return;
    setSaving(true);
    try { await onUpdate(form); setEditing(false); } catch (e) { console.error(e); alert('Update failed'); }
    finally { setSaving(false); }
  };

  const cancel = () => { setForm({ name: cat.name || '', name_kn: cat.name_kn || '', color: cat.color || '#D4AF37' }); setEditing(false); };

  return (
    <div className="group rounded-2xl bg-white border border-[#EAEAE8] p-5 transition-all" data-testid={`category-item-${cat.category_id}`}>
      {editing ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input type="color" value={form.color} onChange={(e) => setForm(p => ({ ...p, color: e.target.value }))} className="w-10 h-10 rounded-lg cursor-pointer shrink-0" data-testid={`edit-color-${cat.category_id}`} />
            <div className="flex-1 space-y-2">
              <Input value={form.name_kn} onChange={(e) => setForm(p => ({ ...p, name_kn: e.target.value }))} placeholder="Kannada name" className="bg-[#F5F5F0] border-[#EAEAE8] font-kannada" data-testid={`edit-name-kn-${cat.category_id}`} />
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="English name" className="bg-[#F5F5F0] border-[#EAEAE8]" data-testid={`edit-name-${cat.category_id}`} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={cancel} className="border-[#EAEAE8]" data-testid={`cancel-edit-${cat.category_id}`}><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#D93025] text-white" data-testid={`save-edit-${cat.category_id}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: cat.color }}>
                <FolderOpen className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-[#1A1A1A] truncate font-kannada">{cat.name_kn || cat.name}</p>
                <p className="text-sm text-[#1A1A1A]/40 truncate">{cat.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="text-[#1A1A1A]/60 hover:text-[#D93025]" data-testid={`rename-category-${cat.category_id}`} title="Rename">
                <Edit3 className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-400 hover:text-red-600" data-testid={`delete-category-${cat.category_id}`} title="Delete">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#EAEAE8] flex items-center justify-between text-sm">
            <span className="text-[#1A1A1A]/40">Articles</span>
            <span className="text-[#1A1A1A] font-medium">{articleCount}</span>
          </div>
        </>
      )}
    </div>
  );
}

// --- Draggable Categories Grid ---
function DraggableCategoriesGrid({ categories, articles, onUpdate, onDelete, onReorder }) {
  const [items, setItems] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    const sorted = (categories || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    setItems(sorted);
  }, [categories]);

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setItems(next);
  };

  const persistOrder = async () => {
    setSavingOrder(true);
    try { await onReorder(items.map(c => c.category_id)); }
    finally { setSavingOrder(false); }
  };

  return (
    <div data-testid="draggable-categories-grid">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#1A1A1A]/60">Drag the handle to reorder. The order here controls how categories appear on the homepage and menus.</p>
        <Button onClick={persistOrder} disabled={savingOrder} size="sm" className="bg-[#D93025] text-white" data-testid="save-categories-order">
          {savingOrder ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save Order
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((cat, idx) => (
          <div
            key={cat.category_id}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== idx) { move(dragIdx, idx); setDragIdx(idx); } }}
            onDragEnd={() => setDragIdx(null)}
            className={`relative ${dragIdx === idx ? 'opacity-50 ring-2 ring-[#D93025]' : ''}`}
            data-testid={`draggable-cat-${cat.category_id}`}
          >
            <span className="absolute top-3 right-3 z-10 cursor-grab active:cursor-grabbing text-[#1A1A1A]/30 bg-white/80 rounded p-1 hover:text-[#D93025]" title="Drag to reorder">
              <GripVertical className="w-4 h-4" />
            </span>
            <CategoryCard
              cat={cat}
              articleCount={articles.filter(a => a.category_id === cat.category_id || (Array.isArray(a.category_ids) && a.category_ids.includes(cat.category_id))).length}
              onUpdate={(data) => onUpdate(cat.category_id, data)}
              onDelete={() => onDelete(cat.category_id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Homepage Layout Tab with Drag & Drop ---
function HomepageLayoutTab({ sections: initialSections, onSave, saving, saved, onReset, categories = [] }) {
  const [sections, setSections] = useState(initialSections);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => { setSections(initialSections); }, [initialSections]);

  // Build a friendly label for each section, replacing generic category_first/popular/rest
  // with the actual Kannada category names so admin knows exactly what's moving.
  const catsSorted = (categories || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const catLabel = (c) => c ? `${c.name_kn || c.name}${c.name && c.name_kn ? ` (${c.name})` : ''}` : '';
  const friendlyLabel = (section) => {
    const id = section?.id || '';
    if (id === 'category_first' && catsSorted[0]) return `${catLabel(catsSorted[0])} — Category Block`;
    if (id === 'category_popular' && catsSorted[1]) return `${catLabel(catsSorted[1])} + Popular Sidebar`;
    if (id === 'category_rest') {
      const rest = catsSorted.slice(2).map(c => c.name_kn || c.name).filter(Boolean);
      return rest.length ? `Other Categories: ${rest.join(' • ')}` : (section.label || 'Remaining Categories');
    }
    // Dynamic per-category section: id is "category_<cat_id>"
    if (id.startsWith('category_')) {
      const cid = id.replace('category_', '');
      const cat = (categories || []).find(c => c.category_id === cid);
      if (cat) return `${catLabel(cat)} — Category Block`;
    }
    return section.label || id;
  };

  const moveSection = (fromIdx, toIdx) => {
    const updated = [...sections];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    const reordered = updated.map((s, i) => ({ ...s, order: i }));
    setSections(reordered);
  };

  const toggleSection = (idx) => {
    const updated = sections.map((s, i) => i === idx ? { ...s, enabled: !s.enabled } : s);
    setSections(updated);
  };

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    moveSection(dragIdx, idx);
    setDragIdx(idx);
  };

  const handleDragEnd = () => { setDragIdx(null); };

  return (
    <div className="max-w-3xl space-y-6" data-testid="homepage-layout-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Homepage Layout</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">Drag to reorder sections. Toggle to enable/disable. Category blocks show your live Kannada category names.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onReset} variant="outline" className="border-[#EAEAE8] text-[#1A1A1A]" data-testid="reset-homepage-layout">
            Reset to current UI defaults
          </Button>
          <Button onClick={() => onSave(sections)} disabled={saving} className="bg-[#D93025] text-white" data-testid="save-homepage-layout">
            {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save Layout</>}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {sections.map((section, idx) => (
          <div
            key={section.id}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-4 p-4 rounded-xl border transition-all cursor-grab active:cursor-grabbing ${
              dragIdx === idx ? 'bg-[#D93025]/10 border-[#D93025]/30 scale-[1.02]' : 
              section.enabled ? 'bg-white border-[#EAEAE8] hover:border-[#EAEAE8]' : 'bg-[#F5F5F0] border-[#EAEAE8] opacity-50'
            }`}
            data-testid={`section-item-${section.id}`}
          >
            <GripVertical className="w-5 h-5 text-[#1A1A1A]/20 flex-shrink-0" />
            <span className="w-8 h-8 rounded-lg bg-[#F5F5F0] flex items-center justify-center text-xs font-bold text-[#1A1A1A]/40">
              {idx + 1}
            </span>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-[#1A1A1A] block truncate font-kannada">{friendlyLabel(section)}</span>
              <span className="text-[11px] text-[#737373] font-mono">{section.id}</span>
            </div>
            <Switch
              checked={section.enabled}
              onCheckedChange={() => toggleSection(idx)}
              data-testid={`toggle-${section.id}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Homepage Slider Manager Tab ---
function SliderManagerTab({ config: initialConfig, onSave, saving, saved }) {
  const [config, setConfig] = useState(initialConfig || { enabled: false, autoplay: true, interval_ms: 5000, article_ids: [] });
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);

  useEffect(() => { setConfig(initialConfig || { enabled: false, autoplay: true, interval_ms: 5000, article_ids: [] }); }, [initialConfig]);

  useEffect(() => {
    setLoadingArticles(true);
    getArticles({ limit: 80, status: 'published' })
      .then(res => setArticles(res.data?.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoadingArticles(false));
  }, []);

  const toggleArticle = (id) => {
    const ids = Array.isArray(config.article_ids) ? [...config.article_ids] : [];
    const i = ids.indexOf(id);
    if (i >= 0) ids.splice(i, 1); else ids.push(id);
    setConfig({ ...config, article_ids: ids });
  };

  const moveArticle = (idx, dir) => {
    const ids = [...(config.article_ids || [])];
    const t = idx + dir;
    if (t < 0 || t >= ids.length) return;
    [ids[idx], ids[t]] = [ids[t], ids[idx]];
    setConfig({ ...config, article_ids: ids });
  };

  const selectedArticles = (config.article_ids || [])
    .map(id => articles.find(a => a.article_id === id))
    .filter(Boolean);

  const unselectedArticles = articles.filter(a => !(config.article_ids || []).includes(a.article_id));

  return (
    <div className="max-w-5xl space-y-6" data-testid="slider-manager-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Homepage Slider</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">Select articles, reorder, and enable the auto-rotating hero carousel.</p>
        </div>
        <Button onClick={() => onSave(config)} disabled={saving} className="bg-[#D93025] text-white" data-testid="save-slider-config">
          {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save Slider</>}
        </Button>
      </div>

      {/* Slider controls */}
      <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled === true}
            onChange={e => setConfig({ ...config, enabled: e.target.checked })}
            className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            data-testid="slider-enabled-toggle"
          />
          <div>
            <span className="text-sm font-bold text-[#1A1A1A]">Enable Slider</span>
            <p className="text-xs text-gray-500">Shows the carousel above the hero section</p>
          </div>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.autoplay !== false}
            onChange={e => setConfig({ ...config, autoplay: e.target.checked })}
            className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            data-testid="slider-autoplay-toggle"
          />
          <div>
            <span className="text-sm font-bold text-[#1A1A1A]">Autoplay</span>
            <p className="text-xs text-gray-500">Auto-advance slides</p>
          </div>
        </label>
        <div>
          <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider mb-2 block">Interval (ms)</label>
          <Input
            type="number" min={2000} max={15000} step={500}
            value={config.interval_ms || 5000}
            onChange={e => setConfig({ ...config, interval_ms: parseInt(e.target.value || '5000', 10) })}
            className="bg-[#F5F5F0] border-[#EAEAE8]"
            data-testid="slider-interval-input"
          />
        </div>
      </div>

      {/* Selected articles (ordered) */}
      <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
        <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">Selected articles ({selectedArticles.length})</h3>
        {selectedArticles.length === 0 ? (
          <p className="text-sm text-[#737373]">No articles selected yet. Pick from the list below.</p>
        ) : (
          <div className="space-y-2">
            {selectedArticles.map((a, idx) => (
              <div key={a.article_id} className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F0] border border-[#EAEAE8]" data-testid={`slider-selected-${a.article_id}`}>
                <span className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-xs font-bold text-[#D93025]">{idx + 1}</span>
                {a.featured_image ? <img src={a.featured_image} alt="" className="w-14 h-10 object-cover rounded" /> : <div className="w-14 h-10 rounded bg-[#EAEAE8]" />}
                <span className="flex-1 text-sm text-[#1A1A1A] line-clamp-1">{a.title_kn || a.title}</span>
                <button type="button" onClick={() => moveArticle(idx, -1)} disabled={idx === 0} className="p-1.5 rounded hover:bg-white disabled:opacity-30" title="Move up"><ChevronUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => moveArticle(idx, 1)} disabled={idx === selectedArticles.length - 1} className="p-1.5 rounded hover:bg-white disabled:opacity-30" title="Move down"><ChevronDown className="w-4 h-4" /></button>
                <button type="button" onClick={() => toggleArticle(a.article_id)} className="p-1.5 rounded text-red-600 hover:bg-red-50" title="Remove"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available articles */}
      <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
        <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">Published articles</h3>
        {loadingArticles ? (
          <p className="text-sm text-[#737373]">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-2">
            {unselectedArticles.map(a => (
              <button
                type="button"
                key={a.article_id}
                onClick={() => toggleArticle(a.article_id)}
                className="flex items-center gap-3 p-2 rounded-xl bg-[#F5F5F0] border border-[#EAEAE8] hover:border-[#D93025] text-left transition-all"
                data-testid={`slider-pick-${a.article_id}`}
              >
                {a.featured_image ? <img src={a.featured_image} alt="" className="w-12 h-9 object-cover rounded" /> : <div className="w-12 h-9 rounded bg-[#EAEAE8]" />}
                <span className="flex-1 text-xs text-[#1A1A1A] line-clamp-2">{a.title_kn || a.title}</span>
                <Plus className="w-4 h-4 text-[#D93025] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Header & Navigation Settings Tab ---
function HeaderSettingsTab({ config: initialConfig, onSave, saving, saved, onReset }) {
  const [config, setConfig] = useState(initialConfig);
  const [menuItems, setMenuItems] = useState(initialConfig.menu_items || []);

  useEffect(() => {
    setConfig(initialConfig);
    setMenuItems(initialConfig.menu_items || []);
  }, [initialConfig]);

  const toggleMenuItem = (idx) => {
    const updated = menuItems.map((item, i) => i === idx ? { ...item, enabled: !item.enabled } : item);
    setMenuItems(updated);
  };

  const updateMenuItem = (idx, field, value) => {
    const updated = menuItems.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    setMenuItems(updated);
  };

  const moveMenuItem = (idx, direction) => {
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === menuItems.length - 1)) return;
    const updated = [...menuItems];
    const targetIdx = idx + direction;
    [updated[idx], updated[targetIdx]] = [updated[targetIdx], updated[idx]];
    setMenuItems(updated.map((item, i) => ({ ...item, order: i })));
  };

  const addMenuItem = () => {
    setMenuItems([...menuItems, { label: 'New Item', label_kn: '', path: '/', enabled: true, order: menuItems.length }]);
  };

  const removeMenuItem = (idx) => {
    setMenuItems(menuItems.filter((_, i) => i !== idx).map((item, i) => ({ ...item, order: i })));
  };

  const handleSave = () => {
    onSave({ ...config, menu_items: menuItems });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="header-settings-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Header & Navigation</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">Configure header behavior and navigation menu items.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#D93025] text-white" data-testid="save-header-settings">
          {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
        </Button>
      </div>

      {/* Sticky Header Toggle */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Header Behavior</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#1A1A1A]/70">Sticky Header</p>
            <p className="text-xs text-[#1A1A1A]/40 mt-1">Header stays visible when scrolling</p>
          </div>
          <Switch
            checked={config.sticky_enabled !== false}
            onCheckedChange={(val) => setConfig(prev => ({ ...prev, sticky_enabled: val }))}
            data-testid="sticky-header-toggle"
          />
        </div>
      </div>

      {/* Logo URL */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Logo</h3>
        <div className="flex items-center gap-4">
          <img src={config.logo_url || '/logo.svg'} alt="Current logo" className="h-12 bg-[#F5F5F0] rounded-lg px-3 py-1" />
          <Input
            value={config.logo_url || '/logo.svg'}
            onChange={(e) => setConfig(prev => ({ ...prev, logo_url: e.target.value }))}
            placeholder="Logo URL or path"
            className="flex-1 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]"
            data-testid="logo-url-input"
          />
        </div>
      </div>

      {/* Menu Items */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[#1A1A1A]">Navigation Menu Items</h3>
          <div className="flex items-center gap-2">
            {typeof onReset === 'function' && (
              <Button size="sm" variant="outline" onClick={onReset} className="border-[#EAEAE8] text-[#1A1A1A]/60" data-testid="reset-header-menu">
                Reset to defaults
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={addMenuItem} className="border-[#EAEAE8] text-[#1A1A1A]/60" data-testid="add-menu-item">
              <Plus className="w-3 h-3 mr-1" />Add
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          {menuItems.map((item, idx) => (
            <div key={idx} className={`flex items-center gap-3 p-3 rounded-xl border ${item.enabled ? 'bg-[#F5F5F0] border-[#EAEAE8]' : 'bg-black/20 border-[#EAEAE8] opacity-60'}`} data-testid={`menu-item-${idx}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveMenuItem(idx, -1)} disabled={idx === 0} className="text-[#1A1A1A]/30 hover:text-[#1A1A1A] disabled:opacity-20">
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button onClick={() => moveMenuItem(idx, 1)} disabled={idx === menuItems.length - 1} className="text-[#1A1A1A]/30 hover:text-[#1A1A1A] disabled:opacity-20">
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
              <Input value={item.label} onChange={(e) => updateMenuItem(idx, 'label', e.target.value)} placeholder="Label" className="w-32 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-sm h-8" />
              <Input value={item.label_kn || ''} onChange={(e) => updateMenuItem(idx, 'label_kn', e.target.value)} placeholder="ಲೇಬಲ್" className="w-32 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-sm h-8 font-kannada" />
              <Input value={item.path} onChange={(e) => updateMenuItem(idx, 'path', e.target.value)} placeholder="/path" className="flex-1 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-sm h-8" />
              <Switch checked={item.enabled} onCheckedChange={() => toggleMenuItem(idx)} />
              <button onClick={() => removeMenuItem(idx)} className="text-red-400/60 hover:text-red-400"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- Breaking News Marquee Settings Tab ---
function BreakingNewsTab({ config: initialConfig, onSave, saving, saved }) {
  const [config, setConfig] = useState(initialConfig);
  const [items, setItems] = useState(initialConfig.items || []);

  useEffect(() => {
    setConfig(initialConfig);
    setItems(initialConfig.items || []);
  }, [initialConfig]);

  const addItem = () => {
    setItems([...items, { text_en: '', text_kn: '' }]);
  };

  const updateItem = (idx, field, value) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    setItems(updated);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    onSave({ ...config, items });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="breaking-news-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Breaking News Marquee</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">Configure the scrolling news ticker on the homepage.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#D93025] text-white" data-testid="save-breaking-news">
          {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
        </Button>
      </div>

      {/* Enable/Disable */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm font-semibold text-[#1A1A1A]">Enable Breaking News Ticker</p>
            <p className="text-xs text-[#1A1A1A]/40 mt-1">Show/hide the scrolling news bar on homepage</p>
          </div>
          <Switch
            checked={config.enabled !== false}
            onCheckedChange={(val) => setConfig(prev => ({ ...prev, enabled: val }))}
            data-testid="breaking-news-toggle"
          />
        </div>

        {/* Speed Control */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-[#1A1A1A]/70">Scroll Speed</p>
            <span className="text-xs text-[#D93025] font-medium">{config.speed || 50}</span>
          </div>
          <Slider
            value={[config.speed || 50]}
            onValueChange={([val]) => setConfig(prev => ({ ...prev, speed: val }))}
            min={10}
            max={100}
            step={5}
            className="w-full"
            data-testid="breaking-news-speed"
          />
          <div className="flex justify-between text-xs text-[#1A1A1A]/30 mt-1">
            <span>Slow</span>
            <span>Fast</span>
          </div>
        </div>
      </div>

      {/* News Items */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[#1A1A1A]">News Items ({items.length})</h3>
          <Button size="sm" variant="outline" onClick={addItem} className="border-[#EAEAE8] text-[#1A1A1A]/60" data-testid="add-breaking-item">
            <Plus className="w-3 h-3 mr-1" />Add Item
          </Button>
        </div>
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-[#F5F5F0] border border-[#EAEAE8]" data-testid={`breaking-item-${idx}`}>
              <span className="w-6 h-6 rounded bg-[#D93025]/20 flex items-center justify-center text-xs text-[#D93025] font-bold flex-shrink-0 mt-1">{idx + 1}</span>
              <div className="flex-1 space-y-2">
                <Input value={item.text_en} onChange={(e) => updateItem(idx, 'text_en', e.target.value)} placeholder="English text" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-sm" />
                <Input value={item.text_kn || ''} onChange={(e) => updateItem(idx, 'text_kn', e.target.value)} placeholder="ಕನ್ನಡ ಪಠ್ಯ" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-sm font-kannada" />
              </div>
              <button onClick={() => removeItem(idx)} className="text-red-400/60 hover:text-red-400 mt-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-center text-[#1A1A1A]/30 py-6 text-sm">No breaking news items. Add one above.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Cricket Cards Manual Manager ---
function CricketCardsTab({ initial, onSave, saving, saved }) {
  const [items, setItems] = useState(initial || []);
  useEffect(() => { setItems(initial || []); }, [initial]);

  const upd = (idx, field, val) => setItems(prev => prev.map((m, i) => i === idx ? { ...m, [field]: val } : m));
  const add = () => setItems(prev => [...prev, { team1: '', score1: '-', team2: '', score2: '-', status: '', match_type: 'T20', venue: '', live: false }]);
  const rm = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= items.length) return;
    const next = [...items];
    [next[idx], next[t]] = [next[t], next[idx]];
    setItems(next);
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="cricket-cards-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Cricket Score Cards</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">Manually create and edit live match cards. These appear in the homepage widgets bar BEFORE the automated cricket feed.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={add} variant="outline" className="border-[#EAEAE8]" size="sm" data-testid="add-cricket-card"><Plus className="w-3 h-3 mr-1" />Add Match</Button>
          <Button onClick={() => onSave(items)} disabled={saving} className="bg-[#D93025] text-white" data-testid="save-cricket-cards">
            {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((m, idx) => (
          <div key={idx} className="rounded-xl bg-white border border-[#EAEAE8] p-4" data-testid={`cricket-card-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5 text-xs text-[#737373]">
                <span className="font-bold">Match #{idx + 1}</span>
                {m.live && <span className="px-1.5 py-0.5 rounded bg-red-500 text-white text-[10px] font-bold animate-pulse">LIVE</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#1A1A1A]/30 hover:text-[#1A1A1A] disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-[#1A1A1A]/30 hover:text-[#1A1A1A] disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => rm(idx)} className="text-red-400 hover:text-red-600" data-testid={`remove-cricket-${idx}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-3">
                <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Team 1</Label>
                <Input value={m.team1 || ''} onChange={(e) => upd(idx, 'team1', e.target.value)} placeholder="IND" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Score 1</Label>
                <Input value={m.score1 || ''} onChange={(e) => upd(idx, 'score1', e.target.value)} placeholder="285/6 (50)" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Team 2</Label>
                <Input value={m.team2 || ''} onChange={(e) => upd(idx, 'team2', e.target.value)} placeholder="AUS" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Score 2</Label>
                <Input value={m.score2 || ''} onChange={(e) => upd(idx, 'score2', e.target.value)} placeholder="120/3 (28.2)" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Format</Label>
                <select value={m.match_type || ''} onChange={(e) => upd(idx, 'match_type', e.target.value)} className="w-full h-8 px-2 rounded border border-[#EAEAE8] text-sm bg-[#F5F5F0]">
                  <option value="">—</option>
                  <option value="T20">T20</option>
                  <option value="ODI">ODI</option>
                  <option value="TEST">TEST</option>
                  <option value="IPL">IPL</option>
                </select>
              </div>
              <div className="col-span-7">
                <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Status / Description</Label>
                <Input value={m.status || ''} onChange={(e) => upd(idx, 'status', e.target.value)} placeholder="AUS needs 166 in 130 balls" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" />
              </div>
              <div className="col-span-3 flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!m.live} onChange={(e) => upd(idx, 'live', e.target.checked)} className="accent-[#D93025]" />
                  Show LIVE badge
                </label>
              </div>
              <div className="col-span-12">
                <Label className="text-[10px] text-[#737373] uppercase tracking-wider">Venue (optional)</Label>
                <Input value={m.venue || ''} onChange={(e) => upd(idx, 'venue', e.target.value)} placeholder="Wankhede Stadium, Mumbai" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" />
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="rounded-xl border border-dashed border-[#EAEAE8] p-10 text-center">
            <Activity className="w-8 h-8 mx-auto text-[#737373] mb-2" />
            <p className="text-sm text-[#737373] mb-3">No manual cricket cards. The widget will show automated feed only.</p>
            <Button onClick={add} className="bg-[#D93025] text-white" size="sm"><Plus className="w-3 h-3 mr-1" />Add first match</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Election Panel Settings Tab ---
function ElectionPanelTab({ config: initialConfig, onSave, saving, saved }) {
  const [config, setConfig] = useState(initialConfig || { enabled: false, parties: [] });
  const [parties, setParties] = useState(initialConfig?.parties || []);
  const [uploadingIdx, setUploadingIdx] = useState(-1);

  useEffect(() => {
    setConfig(initialConfig || { enabled: false, parties: [] });
    setParties(initialConfig?.parties || []);
  }, [initialConfig]);

  const updateParty = (idx, field, value) => {
    setParties(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const addParty = () => {
    setParties(prev => [...prev, { id: `p_${Date.now()}`, name: '', name_kn: '', logo: '', color: '#ffd54f', seats: 0, votes_pct: '' }]);
  };

  const removeParty = (idx) => {
    setParties(prev => prev.filter((_, i) => i !== idx));
  };

  const moveParty = (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= parties.length) return;
    const next = [...parties];
    [next[idx], next[t]] = [next[t], next[idx]];
    setParties(next);
  };

  const handleLogoUpload = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const res = await uploadFile(file);
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      updateParty(idx, 'logo', url);
    } catch (e) {
      alert('Logo upload failed: ' + (e?.response?.data?.detail || e?.message || ''));
    } finally { setUploadingIdx(-1); }
  };

  const handleSave = () => {
    onSave({ ...config, parties });
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="election-panel-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Election Results Panel</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">Manually maintain live election results shown above the homepage hero. Toggle to enable/disable.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#D93025] text-white" data-testid="save-election-panel">
          {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
        </Button>
      </div>

      {/* Master toggle */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A]">Show Election Panel on Homepage</p>
          <p className="text-xs text-[#1A1A1A]/40 mt-1">Renders a live results bar above the hero section. Hidden when off.</p>
        </div>
        <Switch
          checked={config.enabled === true}
          onCheckedChange={(val) => setConfig(prev => ({ ...prev, enabled: val }))}
          data-testid="election-panel-enabled-toggle"
        />
      </div>

      {/* Title / Total seats */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-bold text-[#737373] uppercase tracking-wider mb-2 block">Title (English)</Label>
          <Input value={config.title || ''} onChange={(e) => setConfig(p => ({ ...p, title: e.target.value }))} placeholder="Live Election Results" className="bg-[#F5F5F0] border-[#EAEAE8]" data-testid="election-title" />
        </div>
        <div>
          <Label className="text-xs font-bold text-[#737373] uppercase tracking-wider mb-2 block">Title (ಕನ್ನಡ)</Label>
          <Input value={config.title_kn || ''} onChange={(e) => setConfig(p => ({ ...p, title_kn: e.target.value }))} placeholder="ಚುನಾವಣಾ ಫಲಿತಾಂಶ" className="bg-[#F5F5F0] border-[#EAEAE8] font-kannada" data-testid="election-title-kn" />
        </div>
        <div>
          <Label className="text-xs font-bold text-[#737373] uppercase tracking-wider mb-2 block">Subtitle (Optional)</Label>
          <Input value={config.subtitle || ''} onChange={(e) => setConfig(p => ({ ...p, subtitle: e.target.value }))} placeholder="Karnataka 2028 · Counting underway" className="bg-[#F5F5F0] border-[#EAEAE8]" data-testid="election-subtitle" />
        </div>
        <div>
          <Label className="text-xs font-bold text-[#737373] uppercase tracking-wider mb-2 block">Subtitle (ಕನ್ನಡ)</Label>
          <Input value={config.subtitle_kn || ''} onChange={(e) => setConfig(p => ({ ...p, subtitle_kn: e.target.value }))} className="bg-[#F5F5F0] border-[#EAEAE8] font-kannada" data-testid="election-subtitle-kn" />
        </div>
        <div>
          <Label className="text-xs font-bold text-[#737373] uppercase tracking-wider mb-2 block">Total Seats</Label>
          <Input type="number" value={config.total_seats || 0} onChange={(e) => setConfig(p => ({ ...p, total_seats: parseInt(e.target.value || '0', 10) }))} className="bg-[#F5F5F0] border-[#EAEAE8]" data-testid="election-total-seats" />
        </div>
      </div>

      {/* Parties */}
      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#1A1A1A]">Parties ({parties.length})</h3>
          <Button size="sm" onClick={addParty} variant="outline" className="border-[#EAEAE8]" data-testid="add-party-btn">
            <Plus className="w-3 h-3 mr-1" />Add Party
          </Button>
        </div>
        <div className="space-y-3">
          {parties.map((p, idx) => (
            <div key={idx} className="rounded-xl border border-[#EAEAE8] p-3 grid grid-cols-12 gap-2 items-center" data-testid={`party-row-${idx}`}>
              <div className="col-span-1 flex flex-col items-center gap-1">
                <button onClick={() => moveParty(idx, -1)} disabled={idx === 0} className="text-[#1A1A1A]/30 hover:text-[#1A1A1A] disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                <button onClick={() => moveParty(idx, 1)} disabled={idx === parties.length - 1} className="text-[#1A1A1A]/30 hover:text-[#1A1A1A] disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
              </div>
              <div className="col-span-2 flex flex-col items-center gap-1">
                {p.logo ? (
                  <img src={p.logo} alt="" className="w-12 h-12 rounded-full object-cover bg-white border border-[#EAEAE8]" />
                ) : (
                  <div className="w-12 h-12 rounded-full flex items-center justify-center font-black text-sm" style={{ backgroundColor: p.color || '#ffd54f', color: '#1A1A1A' }}>
                    {(p.name_kn || p.name || '?').charAt(0)}
                  </div>
                )}
                <label className="text-[10px] text-[#D93025] cursor-pointer hover:underline">
                  Upload
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleLogoUpload(idx, e.target.files?.[0])} />
                </label>
                {uploadingIdx === idx && <Loader2 className="w-3 h-3 animate-spin text-[#D93025]" />}
              </div>
              <div className="col-span-3 space-y-1">
                <Input value={p.name_kn || ''} onChange={(e) => updateParty(idx, 'name_kn', e.target.value)} placeholder="ಪಕ್ಷದ ಹೆಸರು" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8 font-kannada" data-testid={`party-name-kn-${idx}`} />
                <Input value={p.name || ''} onChange={(e) => updateParty(idx, 'name', e.target.value)} placeholder="Party name" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" data-testid={`party-name-${idx}`} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-[#737373] block">Color</Label>
                <input type="color" value={p.color || '#ffd54f'} onChange={(e) => updateParty(idx, 'color', e.target.value)} className="w-full h-8 rounded cursor-pointer" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-[#737373] block">Seats</Label>
                <Input type="number" value={p.seats || 0} onChange={(e) => updateParty(idx, 'seats', parseInt(e.target.value || '0', 10))} className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" data-testid={`party-seats-${idx}`} />
              </div>
              <div className="col-span-1">
                <Label className="text-[10px] text-[#737373] block">Vote%</Label>
                <Input value={p.votes_pct || ''} onChange={(e) => updateParty(idx, 'votes_pct', e.target.value)} placeholder="42.1" className="bg-[#F5F5F0] border-[#EAEAE8] text-sm h-8" data-testid={`party-pct-${idx}`} />
              </div>
              <div className="col-span-1 flex justify-center">
                <button onClick={() => removeParty(idx)} className="text-red-400 hover:text-red-600" title="Remove" data-testid={`party-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {parties.length === 0 && (
            <p className="text-center text-[#1A1A1A]/30 py-6 text-sm">No parties yet. Click "Add Party" above.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Weather Widget Settings Tab ---
function WeatherSettingsTab({ config: initialConfig, onSave, saving, saved }) {
  const [config, setConfig] = useState(initialConfig);

  useEffect(() => { setConfig(initialConfig); }, [initialConfig]);

  const handleSave = () => { onSave(config); };

  const CITIES = ['Bengaluru', 'Mysuru', 'Hubballi', 'Mangaluru', 'Belagavi', 'Kalaburagi', 'Davanagere', 'Shivamogga', 'Tumakuru', 'Raichur'];

  return (
    <div className="max-w-3xl space-y-6" data-testid="weather-settings-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Weather Widget</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">Display live weather information in the header.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#D93025] text-white" data-testid="save-weather-settings">
          {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6 space-y-6">
        {/* Enable/Disable */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[#1A1A1A]">Enable Weather Widget</p>
            <p className="text-xs text-[#1A1A1A]/40 mt-1">Shows current weather in header bar</p>
          </div>
          <Switch
            checked={config.enabled !== false}
            onCheckedChange={(val) => setConfig(prev => ({ ...prev, enabled: val }))}
            data-testid="weather-toggle"
          />
        </div>

        {/* City Selection */}
        <div>
          <p className="text-sm text-[#1A1A1A]/70 mb-2">City</p>
          <Select value={config.city || 'Bengaluru'} onValueChange={(val) => setConfig(prev => ({ ...prev, city: val }))}>
            <SelectTrigger className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" data-testid="weather-city-select">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#EAEAE8]">
              {CITIES.map(city => (
                <SelectItem key={city} value={city} className="text-[#1A1A1A] hover:bg-[#EAEAE8]">{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Show in Header Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#1A1A1A]/70">Show in Header Bar</p>
            <p className="text-xs text-[#1A1A1A]/40 mt-1">Display compact weather in the top info bar</p>
          </div>
          <Switch
            checked={config.show_in_header !== false}
            onCheckedChange={(val) => setConfig(prev => ({ ...prev, show_in_header: val }))}
            data-testid="weather-show-header"
          />
        </div>
      </div>
    </div>
  );
}

// --- Podcasts Management Tab ---
function PodcastsTab() {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: '', title_kn: '', description: '', description_kn: '', audio_url: '', embed_url: '', embed_code: '', thumbnail: '', status: 'draft', duration: '' });
  const [uploading, setUploading] = useState('');
  const fileInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const loadPodcasts = () => {
    setLoading(true);
    getPodcasts().then(res => setPodcasts(res.data.podcasts || [])).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { loadPodcasts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (file, type) => {
    setUploading(type);
    try {
      const res = await uploadFile(file);
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm(p => ({ ...p, [type === 'audio' ? 'audio_url' : 'thumbnail']: url }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert('Upload failed: ' + detail);
    }
    finally { setUploading(''); }
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        await updatePodcast(editItem.podcast_id, form);
      } else {
        await createPodcast(form);
      }
      setShowForm(false); setEditItem(null);
      setForm({ title: '', title_kn: '', description: '', description_kn: '', audio_url: '', embed_url: '', embed_code: '', thumbnail: '', status: 'draft', duration: '' });
      loadPodcasts();
    } catch (err) { console.error('Save failed:', err); }
  };

  const handleEdit = (pod) => {
    setEditItem(pod);
    setForm({ title: pod.title, title_kn: pod.title_kn || '', description: pod.description || '', description_kn: pod.description_kn || '', audio_url: pod.audio_url || '', embed_url: pod.embed_url || '', embed_code: pod.embed_code || '', thumbnail: pod.thumbnail || '', status: pod.status || 'draft', duration: pod.duration || '' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this podcast?')) return;
    await deletePodcast(id);
    loadPodcasts();
  };

  return (
    <div className="space-y-6" data-testid="podcasts-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Podcasts</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">{podcasts.length} episodes</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ title: '', title_kn: '', description: '', description_kn: '', audio_url: '', embed_url: '', embed_code: '', thumbnail: '', status: 'draft', duration: '' }); }}
          className="bg-[#D93025] text-white" data-testid="add-podcast-btn">
          <Plus className="w-4 h-4 mr-2" />New Episode
        </Button>
      </div>

      {showForm && (
        <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6 space-y-4" data-testid="podcast-form">
          <h3 className="text-lg font-bold text-[#1A1A1A]">{editItem ? 'Edit' : 'New'} Podcast Episode</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Title (Kannada)</Label><Input value={form.title_kn} onChange={(e) => setForm(p => ({...p, title_kn: e.target.value}))} placeholder="ಶೀರ್ಷಿಕೆ" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] font-kannada" /></div>
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Title (English)</Label><Input value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Title" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Description (Kannada)</Label><textarea value={form.description_kn} onChange={(e) => setForm(p => ({...p, description_kn: e.target.value}))} placeholder="ವಿವರಣೆ" className="w-full min-h-[80px] bg-[#F5F5F0] border border-[#EAEAE8] text-[#1A1A1A] rounded-md p-2 text-sm font-kannada" /></div>
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Description (English)</Label><textarea value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="w-full min-h-[80px] bg-[#F5F5F0] border border-[#EAEAE8] text-[#1A1A1A] rounded-md p-2 text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Audio File</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="border-[#EAEAE8] text-[#1A1A1A]/60">
                  {uploading === 'audio' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}Upload Audio
                </Button>
                {form.audio_url && <span className="text-xs text-emerald-400 truncate max-w-[200px]">Uploaded</span>}
              </div>
              <input ref={fileInputRef} type="file" accept="audio/*" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], 'audio')} className="hidden" />
            </div>
            <div>
              <Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Thumbnail</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => thumbInputRef.current?.click()} className="border-[#EAEAE8] text-[#1A1A1A]/60">
                  {uploading === 'thumbnail' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Image className="w-4 h-4 mr-1" />}Upload Image
                </Button>
                {form.thumbnail && <img src={form.thumbnail} alt="" className="w-8 h-8 rounded object-cover" />}
              </div>
              <input ref={thumbInputRef} type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], 'thumbnail')} className="hidden" />
            </div>
          </div>
          <div className="space-y-2 rounded-xl bg-[#FAFAF8] border border-[#EAEAE8] p-4">
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-purple-600" />
              <Label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">Or embed from Spotify / YouTube / SoundCloud</Label>
            </div>
            <Input value={form.embed_url} onChange={(e) => setForm(p => ({ ...p, embed_url: e.target.value }))} placeholder="https://open.spotify.com/episode/... or https://youtu.be/..." className="bg-white border-[#EAEAE8] text-[#1A1A1A]" data-testid="podcast-embed-url" />
            <textarea value={form.embed_code} onChange={(e) => setForm(p => ({ ...p, embed_code: e.target.value }))} placeholder="Or paste raw <iframe> embed code here (takes precedence over URL)" className="w-full min-h-[60px] bg-white border border-[#EAEAE8] text-[#1A1A1A] rounded-md p-2 text-xs font-mono" data-testid="podcast-embed-code" />
            <p className="text-[11px] text-[#737373]">Tip: on Spotify episode page → Share → Copy Spotify URI or paste the open.spotify.com link.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Duration</Label><Input value={form.duration} onChange={(e) => setForm(p => ({...p, duration: e.target.value}))} placeholder="12:45" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" /></div>
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Status</Label>
              <Select value={form.status} onValueChange={(val) => setForm(p => ({...p, status: val}))}>
                <SelectTrigger className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#EAEAE8]">
                  <SelectItem value="draft" className="text-[#1A1A1A]">Draft</SelectItem>
                  <SelectItem value="published" className="text-[#1A1A1A]">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditItem(null); }} className="border-[#EAEAE8] text-[#1A1A1A]/60">Cancel</Button>
            <Button onClick={handleSave} className="bg-[#D93025] text-white" data-testid="save-podcast-btn"><Save className="w-4 h-4 mr-2" />Save</Button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#D93025] animate-spin" /></div> : (
        <div className="space-y-3">
          {podcasts.map(pod => (
            <div key={pod.podcast_id} className="flex items-center gap-4 p-4 rounded-xl bg-white border border-[#EAEAE8]" data-testid={`podcast-${pod.podcast_id}`}>
              {pod.thumbnail ? <img src={pod.thumbnail} alt="" className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-[#F5F5F0] flex items-center justify-center"><Music className="w-6 h-6 text-[#1A1A1A]/20" /></div>}
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-[#1A1A1A] truncate font-kannada">{pod.title_kn || pod.title}</h4>
                <p className="text-xs text-[#1A1A1A]/40">{pod.duration || 'No duration'} | {new Date(pod.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs ${pod.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{pod.status}</span>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(pod)} className="p-2 rounded-lg hover:bg-[#EAEAE8] text-[#1A1A1A]/40"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(pod.podcast_id)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400/60"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {podcasts.length === 0 && !showForm && <p className="text-center text-[#1A1A1A]/30 py-12">No podcasts yet. Create your first episode.</p>}
        </div>
      )}
    </div>
  );
}

// --- Shorts Management Tab ---
function ShortsTab() {
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: '', title_kn: '', caption: '', caption_kn: '', video_url: '', thumbnail: '', status: 'draft' });
  const [uploading, setUploading] = useState('');
  const vidInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const loadShorts = () => {
    setLoading(true);
    getShorts().then(res => setShorts(res.data.shorts || [])).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { loadShorts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (file, type) => {
    setUploading(type);
    try {
      const res = await uploadFile(file);
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm(p => ({ ...p, [type === 'video' ? 'video_url' : 'thumbnail']: url }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert('Upload failed: ' + detail);
    }
    finally { setUploading(''); }
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        await updateShort(editItem.short_id, form);
      } else {
        await createShort(form);
      }
      setShowForm(false); setEditItem(null);
      setForm({ title: '', title_kn: '', caption: '', caption_kn: '', description: '', description_kn: '', video_url: '', embed_url: '', embed_code: '', thumbnail: '', status: 'draft' });
      loadShorts();
    } catch (err) { console.error('Save failed:', err); }
  };

  const handleEdit = (s) => {
    setEditItem(s);
    setForm({ title: s.title, title_kn: s.title_kn || '', caption: s.caption || '', caption_kn: s.caption_kn || '', description: s.description || '', description_kn: s.description_kn || '', video_url: s.video_url || '', embed_url: s.embed_url || '', embed_code: s.embed_code || '', thumbnail: s.thumbnail || '', status: s.status || 'draft' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this short?')) return;
    await deleteShort(id);
    loadShorts();
  };

  return (
    <div className="space-y-6" data-testid="shorts-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Shorts</h2>
          <p className="text-sm text-[#1A1A1A]/40 mt-1">{shorts.length} videos</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ title: '', title_kn: '', caption: '', caption_kn: '', description: '', description_kn: '', video_url: '', embed_url: '', embed_code: '', thumbnail: '', status: 'draft' }); }}
          className="bg-[#D93025] text-white" data-testid="add-short-btn">
          <Plus className="w-4 h-4 mr-2" />New Short
        </Button>
      </div>

      {showForm && (
        <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6 space-y-4" data-testid="short-form">
          <h3 className="text-lg font-bold text-[#1A1A1A]">{editItem ? 'Edit' : 'New'} Short Video</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Title (Kannada)</Label><Input value={form.title_kn} onChange={(e) => setForm(p => ({...p, title_kn: e.target.value}))} placeholder="ಶೀರ್ಷಿಕೆ" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] font-kannada" /></div>
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Title (English)</Label><Input value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Title" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Caption (Kannada)</Label><Input value={form.caption_kn} onChange={(e) => setForm(p => ({...p, caption_kn: e.target.value}))} placeholder="ಶೀರ್ಷಿಕೆ" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] font-kannada" /></div>
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Caption (English)</Label><Input value={form.caption} onChange={(e) => setForm(p => ({...p, caption: e.target.value}))} placeholder="Caption" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Description (Kannada)</Label><textarea value={form.description_kn} onChange={(e) => setForm(p => ({...p, description_kn: e.target.value}))} placeholder="ವಿವರಣೆ" className="w-full min-h-[80px] bg-[#F5F5F0] border border-[#EAEAE8] text-[#1A1A1A] rounded-md p-2 text-sm font-kannada" data-testid="short-description-kn" /></div>
            <div><Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Description (English)</Label><textarea value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="w-full min-h-[80px] bg-[#F5F5F0] border border-[#EAEAE8] text-[#1A1A1A] rounded-md p-2 text-sm" data-testid="short-description" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Video File</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => vidInputRef.current?.click()} className="border-[#EAEAE8] text-[#1A1A1A]/60">
                  {uploading === 'video' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}Upload Video
                </Button>
                {form.video_url && <span className="text-xs text-emerald-400">Uploaded</span>}
              </div>
              <input ref={vidInputRef} type="file" accept="video/*" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], 'video')} className="hidden" />
            </div>
            <div>
              <Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Thumbnail</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => thumbInputRef.current?.click()} className="border-[#EAEAE8] text-[#1A1A1A]/60">
                  {uploading === 'thumbnail' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Image className="w-4 h-4 mr-1" />}Upload Image
                </Button>
                {form.thumbnail && <img src={form.thumbnail} alt="" className="w-8 h-8 rounded object-cover" />}
              </div>
              <input ref={thumbInputRef} type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], 'thumbnail')} className="hidden" />
            </div>
          </div>
          <div className="space-y-2 rounded-xl bg-[#FAFAF8] border border-[#EAEAE8] p-4">
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-red-600" />
              <Label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">Or embed from YouTube Shorts / Instagram / TikTok</Label>
            </div>
            <Input value={form.embed_url} onChange={(e) => setForm(p => ({ ...p, embed_url: e.target.value }))} placeholder="https://www.youtube.com/shorts/... or Instagram reel URL" className="bg-white border-[#EAEAE8] text-[#1A1A1A]" data-testid="short-embed-url" />
            <textarea value={form.embed_code} onChange={(e) => setForm(p => ({ ...p, embed_code: e.target.value }))} placeholder="Or paste raw <iframe> embed code here" className="w-full min-h-[60px] bg-white border border-[#EAEAE8] text-[#1A1A1A] rounded-md p-2 text-xs font-mono" data-testid="short-embed-code" />
          </div>
          <div>
            <Label className="text-xs text-[#1A1A1A]/40 mb-1 block">Status</Label>
            <Select value={form.status} onValueChange={(val) => setForm(p => ({...p, status: val}))}>
              <SelectTrigger className="w-48 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#EAEAE8]">
                <SelectItem value="draft" className="text-[#1A1A1A]">Draft</SelectItem>
                <SelectItem value="published" className="text-[#1A1A1A]">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditItem(null); }} className="border-[#EAEAE8] text-[#1A1A1A]/60">Cancel</Button>
            <Button onClick={handleSave} className="bg-[#D93025] text-white" data-testid="save-short-btn"><Save className="w-4 h-4 mr-2" />Save</Button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#D93025] animate-spin" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shorts.map(s => (
            <div key={s.short_id} className="rounded-xl bg-white border border-[#EAEAE8] overflow-hidden" data-testid={`short-${s.short_id}`}>
              {s.thumbnail ? <img src={s.thumbnail} alt="" className="w-full h-40 object-cover" /> : 
                <div className="w-full h-40 bg-[#F5F5F0] flex items-center justify-center"><Play className="w-10 h-10 text-[#1A1A1A]/20" /></div>}
              <div className="p-4">
                <h4 className="text-sm font-semibold text-[#1A1A1A] truncate font-kannada">{s.title_kn || s.title}</h4>
                <p className="text-xs text-[#1A1A1A]/40 mt-1 truncate">{s.caption_kn || s.caption || 'No caption'}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className={`px-2 py-1 rounded text-xs ${s.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{s.status}</span>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(s)} className="p-1.5 rounded hover:bg-[#EAEAE8] text-[#1A1A1A]/40"><Edit3 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(s.short_id)} className="p-1.5 rounded hover:bg-red-500/10 text-red-400/60"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {shorts.length === 0 && !showForm && <p className="text-center text-[#1A1A1A]/30 py-12 col-span-full">No shorts yet. Create your first video.</p>}
        </div>
      )}
    </div>
  );
}

function Sidebar({ activeTab, setActiveTab, user }) {
  const { logout } = useAuth();
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'articles', label: 'Articles', icon: FileText },
    { id: 'categories', label: 'Categories', icon: FolderOpen },
    { id: 'tags', label: 'Tags', icon: Tags },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'podcasts', label: 'Podcasts', icon: Headphones },
    { id: 'shorts', label: 'Shorts', icon: Video },
    { id: 'homepage', label: 'Homepage Layout', icon: Menu },
    { id: 'slider', label: 'Homepage Slider', icon: Image },
    { id: 'header', label: 'Header & Nav', icon: Globe },
    { id: 'breaking', label: 'Breaking News', icon: Megaphone },
    { id: 'cricket', label: 'Cricket Cards', icon: Activity },
    { id: 'election', label: 'Election Panel', icon: Vote },
    { id: 'weather', label: 'Weather Widget', icon: CloudSun },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    { id: 'media', label: 'Media Library', icon: Image },
    { id: 'settings', label: 'General Settings', icon: Settings },
  ];

  const handleLogout = async () => {
    if (!window.confirm('Sign out of the admin panel?')) return;
    try { await logout(); } catch (e) { console.error(e); }
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-white border-r border-[#EAEAE8] z-40 flex flex-col" data-testid="admin-sidebar">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-[#EAEAE8] shrink-0">
        <Link to="/" className="flex items-center gap-2" data-testid="admin-home-link">
          <img src="/logo.svg" alt="Taaynadu" className="h-8" />
        </Link>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-[#EAEAE8] shrink-0">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F0]">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#D93025] flex items-center justify-center">
              <span className="text-white font-bold">{(user?.name || 'A')[0]}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1A1A1A] truncate">{user?.name || 'Admin'}</p>
            <div className="flex items-center gap-1">
              <Crown className="w-3 h-3 text-[#D93025]" />
              <span className="text-xs text-[#D93025] capitalize">{user?.role || 'admin'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation - scrollable */}
      <nav className="p-4 space-y-1 flex-1 overflow-y-auto" data-testid="admin-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left ${
              activeTab === item.id 
                ? 'bg-[#D93025]/10 text-[#D93025] border-l-2 border-[#D93025]' 
                : 'text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F5F0]'
            }`}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className="w-5 h-5 shrink-0" />
            <span className="truncate">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Quick Actions */}
      <div className="p-4 border-t border-[#EAEAE8] shrink-0 space-y-2">
        <Link to="/editor/write" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#D93025] text-white font-semibold hover:bg-[#B71C1C] transition-all" data-testid="new-article-btn">
          <Plus className="w-4 h-4" />
          New Article
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-[#EAEAE8] text-[#4A4A4A] hover:text-[#D93025] hover:border-[#D93025]/40 text-sm font-medium transition-all"
          data-testid="admin-logout-btn"
        >
          <X className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

function StatCard({ label, value, icon: Icon, trend, color, subtitle }) {
  // trend is expected to be a signed number (e.g. +12 / -3) representing the
  // week-over-week delta computed by the backend. When null/undefined we render
  // nothing rather than fake data.
  const hasTrend = typeof trend === 'number' && !Number.isNaN(trend);
  const trendUp = hasTrend && trend >= 0;
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-[#EAEAE8] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">{label}</p>
          <p className="text-3xl font-bold text-[#1A1A1A] mt-2">{(value || 0).toLocaleString()}</p>
          {hasTrend ? (
            <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trendUp ? 'text-emerald-500' : 'text-red-500'}`} data-testid={`trend-${label.toLowerCase().replace(/\s/g, '-')}`}>
              {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {trendUp ? '+' : ''}{trend} this week
            </div>
          ) : subtitle ? (
            <p className="mt-2 text-xs text-[#1A1A1A]/40">{subtitle}</p>
          ) : null}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {/* Decorative gradient */}
      <div className={`absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-gradient-to-br ${color} opacity-10 blur-2xl`} />
    </div>
  );
}

// ── Newsletter Management Tab ──
function NewsletterTab() {
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getSubscribers({ page, limit: 20 });
      setSubs(res.data.subscribers || []);
      setTotal(res.data.total || 0);
      setActive(res.data.active || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (email) => {
    await toggleSubscriber(email);
    load();
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    await removeSubscriber(email);
    load();
  };

  return (
    <div className="space-y-4" data-testid="newsletter-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#EAEAE8] rounded-xl p-5">
          <p className="text-xs text-gray-500 uppercase">Total Subscribers</p>
          <p className="text-3xl font-bold text-[#1A1A1A] mt-1">{total}</p>
        </div>
        <div className="bg-white border border-[#EAEAE8] rounded-xl p-5">
          <p className="text-xs text-gray-500 uppercase">Active</p>
          <p className="text-3xl font-bold text-green-600 mt-1">{active}</p>
        </div>
        <div className="bg-white border border-[#EAEAE8] rounded-xl p-5">
          <p className="text-xs text-gray-500 uppercase">Inactive</p>
          <p className="text-3xl font-bold text-gray-400 mt-1">{total - active}</p>
        </div>
      </div>
      <div className="bg-white border border-[#EAEAE8] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-[#EAEAE8]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Joined</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            ) : subs.length === 0 ? (
              <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-400">No subscribers yet</td></tr>
            ) : subs.map(s => (
              <tr key={s.email} className="hover:bg-gray-50" data-testid={`sub-${s.email}`}>
                <td className="px-4 py-3 font-medium text-[#1A1A1A]">{s.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${s.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {s.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => handleToggle(s.email)} className="text-xs font-bold text-blue-600 hover:underline">{s.active !== false ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => handleRemove(s.email)} className="text-xs font-bold text-red-600 hover:underline">Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Media Library Tab ──
function MediaLibraryTab() {
  const [media, setMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 24 };
      if (filter) params.media_type = filter;
      const res = await getMedia(params);
      setMedia(res.data.media || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    let okCount = 0, failCount = 0;
    for (const file of files) {
      try {
        await uploadFile(file);
        okCount += 1;
      } catch (err) {
        failCount += 1;
        console.error('Upload failed for', file.name, err);
      }
    }
    setUploading(false);
    if (failCount > 0) {
      alert(`${okCount} uploaded, ${failCount} failed.`);
    }
    // Reset input so re-selecting same file works
    e.target.value = '';
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media file?')) return;
    try {
      await deleteMediaItem(id);
      load();
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-4" data-testid="media-library-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }} className="h-9 px-3 rounded-lg border border-[#EAEAE8] text-sm bg-white">
            <option value="">All Media ({total})</option>
            <option value="image">Images</option>
            <option value="audio">Audio</option>
            <option value="video">Video</option>
          </select>
        </div>
        <label className="cursor-pointer">
          <input type="file" multiple className="hidden" onChange={handleUpload} accept="image/*,audio/*,video/*" />
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#D93025] text-white font-bold text-sm hover:bg-[#B71C1C] transition-colors">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Upload'}
          </span>
        </label>
      </div>
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="aspect-square bg-gray-200 rounded-xl animate-pulse" />)}
        </div>
      ) : media.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No media files yet. Upload your first file!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {media.map(m => (
            <div key={m.media_id} className="group relative bg-white border border-[#EAEAE8] rounded-xl overflow-hidden" data-testid={`media-${m.media_id}`}>
              {m.media_type === 'image' ? (
                <img src={m.url} alt={m.original_name} className="w-full aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                  {m.media_type === 'audio' ? <Music className="w-8 h-8 text-purple-400" /> : <Video className="w-8 h-8 text-blue-400" />}
                </div>
              )}
              <div className="p-2">
                <p className="text-[10px] font-bold text-[#1A1A1A] truncate">{m.original_name}</p>
                <p className="text-[9px] text-gray-400">{(m.size / 1024).toFixed(0)} KB</p>
              </div>
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleDelete(m.media_id)} className="p-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700" title="Delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const config = ROLE_HIERARCHY[role] || ROLE_HIERARCHY.reader;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${config.color} text-white`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [articles, setArticles] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Forms
  const [newCat, setNewCat] = useState({ name: '', name_kn: '', icon: 'Landmark', color: '#D4AF37' });
  const [newTag, setNewTag] = useState({ name: '', name_kn: '' });

  // Site Settings
  const [siteSettings, setSiteSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
    loadSettings();
  }, [user, authLoading, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = async () => {
    try {
      const res = await getSiteSettings();
      setSiteSettings(res.data);
    } catch (err) { console.error('Settings load error:', err); }
  };

  const saveSiteSettings = async (key, value) => {
    setSettingsSaving(true);
    try {
      if (key === 'homepage_sections') {
        await updateHomepageSections(value);
      } else if (key === 'header') {
        await updateHeaderSettings(value);
      } else if (key === 'breaking_news') {
        await updateBreakingNewsSettings(value);
      } else if (key === 'weather') {
        await updateWeatherSettings(value);
      } else if (key === 'slider') {
        await updateSliderSettings(value);
      } else if (key === 'election_panel') {
        await updateElectionPanelSettings(value);
      }
      setSiteSettings(prev => ({ ...prev, [key]: value }));
      setSettingsSaved(key);
      setTimeout(() => setSettingsSaved(''), 2000);
    } catch (err) { console.error('Save error:', err); }
    finally { setSettingsSaving(false); }
  };

  const handleResetHomepageSections = async () => {
    if (!window.confirm('Reset homepage layout to current UI defaults? This will overwrite your custom order.')) return;
    try {
      const res = await resetHomepageSections();
      setSiteSettings(prev => ({ ...prev, homepage_sections: res.data.sections }));
      setSettingsSaved('homepage_sections');
      setTimeout(() => setSettingsSaved(''), 2000);
    } catch (err) { alert('Reset failed: ' + (err?.response?.data?.detail || err.message)); }
  };

  const handleResetHeaderMenu = async () => {
    if (!window.confirm('Reset header menu to current UI defaults?')) return;
    try {
      const res = await resetHeaderMenu();
      setSiteSettings(prev => ({ ...prev, header: { ...(prev.header || {}), menu_items: res.data.menu_items } }));
      setSettingsSaved('header');
      setTimeout(() => setSettingsSaved(''), 2000);
    } catch (err) { alert('Reset failed: ' + (err?.response?.data?.detail || err.message)); }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, artRes, usrRes, catRes, tagRes] = await Promise.all([
        getAdminDashboard(), getAdminArticles({}), getUsers(), getCategories(), getTags()
      ]);
      setDashboard(dashRes.data);
      setArticles(artRes.data.articles || []);
      setUsers(usrRes.data || []);
      setCategories(catRes.data || []);
      setTags(tagRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await deleteArticle(id);
    setArticles(prev => prev.filter(a => a.article_id !== id));
  };

  const handleRoleChange = async (userId, role) => {
    await updateUserRole(userId, role);
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role } : u));
  };

  const handleCreateCategory = async () => {
    if (!newCat.name) return;
    await createCategory(newCat);
    setNewCat({ name: '', name_kn: '', icon: 'Landmark', color: '#D4AF37' });
    const res = await getCategories();
    setCategories(res.data || []);
  };

  const handleUpdateCategory = async (id, data) => {
    await updateCategory(id, data);
    const res = await getCategories();
    setCategories(res.data || []);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await deleteCategory(id);
    setCategories(prev => prev.filter(c => c.category_id !== id));
  };

  const handleReorderCategories = async (orderedIds) => {
    try {
      await reorderCategories(orderedIds);
      // Refresh from server so order field is in sync
      const res = await getCategories();
      setCategories(res.data || []);
      setSettingsSaved('categories_order');
      setTimeout(() => setSettingsSaved(''), 2000);
    } catch (err) {
      alert('Reorder failed: ' + (err?.response?.data?.detail || err?.message || ''));
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.name) return;
    await createTag(newTag);
    setNewTag({ name: '', name_kn: '' });
    const res = await getTags();
    setTags(res.data || []);
  };

  const handleDeleteTag = async (id) => {
    await deleteTag(id);
    setTags(prev => prev.filter(t => t.tag_id !== id));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D93025]/30 border-t-[#D93025] rounded-full animate-spin" />
      </div>
    );
  }

  const filteredArticles = articles.filter(a => 
    a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.author?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="admin-dashboard">
      <Sidebar activeTab={tab} setActiveTab={setTab} user={user} />

      {/* Main Content */}
      <main className="ml-64 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-sm border-b border-[#EAEAE8] flex items-center justify-between px-6" data-testid="admin-topbar">
          <div className="flex items-center gap-4 min-w-0">
            <h1 className="text-lg font-bold text-[#1A1A1A] capitalize truncate" data-testid="admin-active-tab">{tab.replace(/-/g, ' ')}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/30" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-64 h-10 pl-10 pr-4 rounded-xl bg-[#F5F5F0] border border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373] text-sm focus:outline-none focus:border-[#D93025]/50"
                data-testid="admin-search"
              />
            </div>
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-[#EAEAE8] text-[#4A4A4A] hover:text-[#D93025] hover:border-[#D93025]/40 text-sm font-medium transition-colors"
              data-testid="view-site-btn"
              title="Open live site in a new tab"
            >
              <ExternalLink className="w-4 h-4" />
              View site
            </a>
            <Link
              to="/editor/write"
              className="hidden md:inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#D93025] hover:bg-[#B71C1C] text-white font-semibold text-sm transition-colors"
              data-testid="topbar-new-article-btn"
            >
              <Plus className="w-4 h-4" />
              New
            </Link>
          </div>
        </header>

        <div className="p-6">
          {/* Dashboard Tab */}
          {tab === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard label="Total Articles" value={dashboard?.total_articles} icon={FileText} trend={dashboard?.trends?.articles} color="from-red-500 to-red-600" />
                <StatCard label="Published" value={dashboard?.published} icon={Eye} trend={dashboard?.trends?.published} color="from-emerald-500 to-teal-600" />
                <StatCard label="Drafts" value={dashboard?.drafts} icon={Edit3} color="from-amber-500 to-orange-600" />
                <StatCard label="Scheduled" value={dashboard?.scheduled} icon={Calendar} color="from-purple-500 to-indigo-600" />
                <StatCard label="Media Files" value={dashboard?.total_media} icon={Image} color="from-pink-500 to-rose-600" />
                <StatCard label="Podcasts" value={dashboard?.total_podcasts} icon={Headphones} color="from-orange-500 to-amber-600" />
                <StatCard label="Shorts" value={dashboard?.total_shorts} icon={Video} color="from-cyan-500 to-blue-600" />
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard label="Total Users" value={dashboard?.total_users} icon={Users} trend={dashboard?.trends?.users} color="from-blue-500 to-indigo-600" />
                <StatCard label="Comments" value={dashboard?.total_comments} icon={MessageSquare} color="from-teal-500 to-cyan-600" />
                <StatCard label="Subscribers" value={dashboard?.total_subscribers} icon={Mail} trend={dashboard?.trends?.subscribers} color="from-violet-500 to-purple-600" />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Activity Chart */}
                <div className="lg:col-span-2 rounded-2xl bg-white border border-[#EAEAE8] p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-[#1A1A1A]">Content Activity</h3>
                    <select className="bg-[#F5F5F0] border border-[#EAEAE8] rounded-lg px-3 py-1.5 text-sm text-[#1A1A1A]/60">
                      <option>Last 7 days</option>
                      <option>Last 30 days</option>
                    </select>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={dashboard?.activity_data || []}>
                      <defs>
                        <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#D93025" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="#D93025" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAEAE8" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#737373' }} axisLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#737373' }} axisLine={false} />
                      <Tooltip 
                        contentStyle={{ background: '#ffffff', border: '1px solid #EAEAE8', borderRadius: '12px', color: '#1A1A1A' }}
                        labelStyle={{ color: '#1A1A1A' }}
                      />
                      <Area type="monotone" dataKey="articles" stroke="#D93025" strokeWidth={2} fill="url(#activityGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Category Distribution */}
                <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                  <h3 className="font-bold text-[#1A1A1A] mb-6">Categories</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={dashboard?.category_distribution || []} dataKey="count" nameKey="category" cx="50%" cy="50%" innerRadius={50} outerRadius={80}>
                        {(dashboard?.category_distribution || []).map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #EAEAE8', borderRadius: '12px', color: '#1A1A1A' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {(dashboard?.category_distribution || []).slice(0, 4).map((cat, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-[#1A1A1A]/60">{cat.category}</span>
                        </div>
                        <span className="text-[#1A1A1A] font-medium">{cat.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Activity & Top Articles */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Articles */}
                <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-[#1A1A1A]">Top Performing Articles</h3>
                    <Button variant="ghost" size="sm" className="text-[#D93025] hover:text-[#D93025]">View All</Button>
                  </div>
                  <div className="space-y-4">
                    {(dashboard?.top_articles || []).slice(0, 5).map((art, i) => (
                      <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-[#F5F5F0] hover:bg-[#EAEAE8] transition-colors">
                        <span className="w-8 h-8 rounded-lg bg-[#D93025]/10 flex items-center justify-center text-[#D93025] font-bold text-sm">
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A] truncate">{art.title}</p>
                          <p className="text-xs text-[#1A1A1A]/40">{art.views?.toLocaleString()} views</p>
                        </div>
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-[#1A1A1A]">Recent Activity</h3>
                  </div>
                  <div className="space-y-3">
                    {(dashboard?.recent_activity || []).slice(0, 6).map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F0]">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          item.status === 'published' ? 'bg-emerald-500/20' :
                          item.status === 'scheduled' ? 'bg-purple-500/20' :
                          'bg-amber-500/20'
                        }`}>
                          {item.status === 'published' ? <Eye className="w-4 h-4 text-emerald-400" /> :
                           item.status === 'scheduled' ? <Clock className="w-4 h-4 text-purple-400" /> :
                           <Edit3 className="w-4 h-4 text-amber-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#1A1A1A] truncate">{item.title}</p>
                          <p className="text-xs text-[#1A1A1A]/40">by {item.author_name || 'Unknown'}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.status === 'published' ? 'bg-emerald-500/10 text-emerald-400' :
                          item.status === 'scheduled' ? 'bg-purple-500/10 text-purple-400' :
                          'bg-amber-500/10 text-amber-400'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Third Row - Scheduled & Role Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upcoming Scheduled */}
                <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-[#1A1A1A] flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-400" />
                      Upcoming Scheduled
                    </h3>
                  </div>
                  {(dashboard?.upcoming_scheduled || []).length === 0 ? (
                    <p className="text-[#1A1A1A]/40 text-sm text-center py-8">No scheduled articles</p>
                  ) : (
                    <div className="space-y-3">
                      {(dashboard?.upcoming_scheduled || []).map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
                          <Clock className="w-5 h-5 text-purple-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#1A1A1A] truncate">{item.title}</p>
                            <p className="text-xs text-purple-400">
                              {new Date(item.scheduled_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Role Distribution */}
                <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                  <h3 className="font-bold text-[#1A1A1A] mb-6">User Roles Hierarchy</h3>
                  <div className="space-y-4">
                    {Object.entries(ROLE_HIERARCHY).reverse().map(([role, config]) => {
                      const count = users.filter(u => u.role === role).length;
                      const Icon = config.icon;
                      return (
                        <div key={role} className="flex items-center gap-4 p-4 rounded-xl bg-[#F5F5F0]">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-[#1A1A1A]" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-[#1A1A1A]">{config.label}</span>
                              <span className="text-lg font-bold text-[#1A1A1A]">{count}</span>
                            </div>
                            <div className="mt-2 h-1.5 bg-[#EAEAE8] rounded-full overflow-hidden">
                              <div 
                                className={`h-full bg-gradient-to-r ${config.color} rounded-full transition-all`}
                                style={{ width: `${(count / Math.max(users.length, 1)) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Articles Tab */}
          {tab === 'articles' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="outline" className="border-[#EAEAE8] text-[#1A1A1A]/60 hover:text-[#1A1A1A]">
                    <Filter className="w-4 h-4 mr-2" />Filters
                  </Button>
                  <Button variant="outline" className="border-[#EAEAE8] text-[#1A1A1A]/60 hover:text-[#1A1A1A]">
                    <Download className="w-4 h-4 mr-2" />Export
                  </Button>
                </div>
                <Link to="/editor/write">
                  <Button className="bg-[#D93025] text-white">
                    <Plus className="w-4 h-4 mr-2" />New Article
                  </Button>
                </Link>
              </div>

              {/* Articles Table */}
              <div className="rounded-2xl bg-white border border-[#EAEAE8] overflow-hidden">
                <table className="w-full" data-testid="articles-table">
                  <thead>
                    <tr className="border-b border-[#EAEAE8]">
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Article</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Author</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Views</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Date</th>
                      <th className="text-right px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredArticles.map(art => (
                      <tr key={art.article_id} className="hover:bg-[#F5F5F0] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {art.featured_image && (
                              <img src={art.featured_image} alt="" className="w-12 h-12 rounded-lg object-cover" onError={(e) => { e.target.style.display='none'; }} />
                            )}
                            <div>
                              <p className="text-sm font-medium text-[#1A1A1A] line-clamp-1">{art.title}</p>
                              <p className="text-xs text-[#1A1A1A]/40">{(() => {
                                const cids = Array.isArray(art.category_ids) && art.category_ids.length ? art.category_ids : (art.category_id ? [art.category_id] : []);
                                if (!cids.length) return 'Uncategorized';
                                const lookup = (id) => categories.find(c => c.category_id === id);
                                const primary = lookup(cids[0]);
                                const primaryName = primary ? (primary.name_kn || primary.name) : 'Uncategorized';
                                const extra = cids.length - 1;
                                return extra > 0 ? `${primaryName} + ${extra} more` : primaryName;
                              })()}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {art.author?.picture ? (
                              <img src={art.author.picture} alt="" className="w-6 h-6 rounded-full" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-[#EAEAE8]" />
                            )}
                            <span className="text-sm text-[#1A1A1A]/60">{art.author?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                            art.status === 'published' 
                              ? 'bg-emerald-500/10 text-emerald-400' 
                              : 'bg-amber-500/10 text-amber-400'
                          }`}>
                            {art.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-[#1A1A1A]/60">{(art.views || 0).toLocaleString()}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-[#1A1A1A]/40">
                            {art.created_at ? new Date(art.created_at).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" className="text-[#1A1A1A]/40 hover:text-[#1A1A1A]" onClick={() => window.open(`/article/${art.slug || art.article_id}`, '_blank')}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-[#1A1A1A]/40 hover:text-[#1A1A1A]" onClick={() => navigate(`/editor/write?edit=${art.article_id}`)}>
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteArticle(art.article_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Categories Tab */}
          {tab === 'categories' && (
            <div className="space-y-6">
              {/* Add Category Form */}
              <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                <h3 className="font-bold text-[#1A1A1A] mb-4">Add New Category</h3>
                <div className="flex flex-wrap gap-4">
                  <Input 
                    value={newCat.name} 
                    onChange={(e) => setNewCat(p => ({...p, name: e.target.value}))} 
                    placeholder="Category Name (English)" 
                    className="w-56 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]"
                    data-testid="new-category-name"
                  />
                  <Input 
                    value={newCat.name_kn} 
                    onChange={(e) => setNewCat(p => ({...p, name_kn: e.target.value}))} 
                    placeholder="ವರ್ಗದ ಹೆಸರು (ಕನ್ನಡ)" 
                    className="w-56 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373] font-kannada"
                    data-testid="new-category-name-kn"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#1A1A1A]/40">Color:</span>
                    <input 
                      type="color" 
                      value={newCat.color} 
                      onChange={(e) => setNewCat(p => ({...p, color: e.target.value}))} 
                      className="w-10 h-10 rounded-lg cursor-pointer"
                    />
                  </div>
                  <Button onClick={handleCreateCategory} className="bg-[#D93025] text-white" data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" />Add Category
                  </Button>
                </div>
              </div>

              {/* Categories Grid - draggable to reorder */}
              <DraggableCategoriesGrid
                categories={categories}
                articles={articles}
                onUpdate={handleUpdateCategory}
                onDelete={handleDeleteCategory}
                onReorder={handleReorderCategories}
              />
            </div>
          )}

          {/* Tags Tab */}
          {tab === 'tags' && (
            <div className="space-y-6">
              {/* Add Tag Form */}
              <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                <h3 className="font-bold text-[#1A1A1A] mb-4">Add New Tag</h3>
                <div className="flex gap-4">
                  <Input 
                    value={newTag.name} 
                    onChange={(e) => setNewTag(p => ({...p, name: e.target.value}))} 
                    placeholder="Tag name" 
                    className="w-56 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]"
                    data-testid="new-tag-name"
                  />
                  <Input 
                    value={newTag.name_kn} 
                    onChange={(e) => setNewTag(p => ({...p, name_kn: e.target.value}))} 
                    placeholder="ಟ್ಯಾಗ್ ಹೆಸರು" 
                    className="w-56 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373] font-kannada"
                    data-testid="new-tag-name-kn"
                  />
                  <Button onClick={handleCreateTag} className="bg-[#D93025] text-white" data-testid="add-tag-btn">
                    <Plus className="w-4 h-4 mr-2" />Add Tag
                  </Button>
                </div>
              </div>

              {/* Tags Grid */}
              <div className="flex flex-wrap gap-3">
                {tags.map(tag => (
                  <div key={tag.tag_id} className="group flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white border border-[#EAEAE8] hover:border-[#D93025]/30 transition-all" data-testid={`tag-item-${tag.tag_id}`}>
                    <Tags className="w-4 h-4 text-[#D93025]" />
                    <span className="text-sm text-[#1A1A1A]">{tag.name}</span>
                    {tag.name_kn && <span className="text-sm text-[#1A1A1A]/40 font-kannada">{tag.name_kn}</span>}
                    <button 
                      onClick={() => handleDeleteTag(tag.tag_id)} 
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all ml-2"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {tab === 'users' && (
            <div className="space-y-6">
              {/* Role Hierarchy Info */}
              <div className="rounded-2xl bg-[#D93025]/10 border border-[#D93025]/20 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D93025]/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#D93025]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A1A1A] mb-2">Role Hierarchy</h3>
                    <p className="text-sm text-[#1A1A1A]/60 mb-4">Manage user permissions based on their assigned roles. Higher roles have more permissions.</p>
                    <div className="flex items-center gap-6 text-sm">
                      {Object.entries(ROLE_HIERARCHY).reverse().map(([role, config], i) => {
                        const Icon = config.icon;
                        return (
                          <div key={role} className="flex items-center gap-2">
                            <Icon className="w-4 h-4" style={{ color: config.color.includes('amber') ? '#D4AF37' : config.color.includes('purple') ? '#9333EA' : config.color.includes('blue') ? '#0369A1' : '#64748B' }} />
                            <span className="text-[#1A1A1A]/60">{config.label}</span>
                            {i < 3 && <ChevronRight className="w-4 h-4 text-[#1A1A1A]/20" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Users Table */}
              <div className="rounded-2xl bg-white border border-[#EAEAE8] overflow-hidden">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr className="border-b border-[#EAEAE8]">
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">User</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Email</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Current Role</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Joined</th>
                      <th className="text-right px-6 py-4 text-xs font-medium text-[#1A1A1A]/40 uppercase tracking-wider">Change Role</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="hover:bg-[#F5F5F0] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-10 h-10 rounded-full ring-2 ring-white/10" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-[#D93025] flex items-center justify-center">
                                <span className="text-[#1A1A1A] font-bold">{u.name?.[0]}</span>
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-[#1A1A1A]">{u.name}</p>
                              <p className="text-xs text-[#1A1A1A]/40">{u.user_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-[#1A1A1A]/60">{u.email}</span>
                        </td>
                        <td className="px-6 py-4">
                          <RoleBadge role={u.role} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-[#1A1A1A]/40">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Select value={u.role} onValueChange={(val) => handleRoleChange(u.user_id, val)}>
                            <SelectTrigger className="w-40 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" data-testid={`role-select-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-[#EAEAE8]">
                              <SelectItem value="reader" className="text-[#1A1A1A] hover:bg-[#EAEAE8]">
                                <div className="flex items-center gap-2">
                                  <Eye className="w-4 h-4" />Reader
                                </div>
                              </SelectItem>
                              <SelectItem value="author" className="text-[#1A1A1A] hover:bg-[#EAEAE8]">
                                <div className="flex items-center gap-2">
                                  <BookOpen className="w-4 h-4" />Author
                                </div>
                              </SelectItem>
                              <SelectItem value="editor" className="text-[#1A1A1A] hover:bg-[#EAEAE8]">
                                <div className="flex items-center gap-2">
                                  <PenTool className="w-4 h-4" />Editor
                                </div>
                              </SelectItem>
                              <SelectItem value="admin" className="text-[#1A1A1A] hover:bg-[#EAEAE8]">
                                <div className="flex items-center gap-2">
                                  <Crown className="w-4 h-4" />Admin
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {tab === 'settings' && (
            <div className="max-w-2xl space-y-6">
              <div className="rounded-2xl bg-white border border-[#EAEAE8] p-6">
                <h3 className="font-bold text-[#1A1A1A] mb-4">General Settings</h3>
                <p className="text-[#1A1A1A]/40 text-sm">Use the sidebar tabs for Homepage Layout, Header & Navigation, Breaking News, Weather Widget, Podcasts, and Shorts management.</p>
              </div>
            </div>
          )}

          {/* Podcasts Tab */}
          {tab === 'podcasts' && <PodcastsTab />}

          {/* Shorts Tab */}
          {tab === 'shorts' && <ShortsTab />}

          {/* Homepage Layout Tab */}
          {tab === 'homepage' && siteSettings && (
            <HomepageLayoutTab
              sections={siteSettings.homepage_sections || []}
              onSave={(sections) => saveSiteSettings('homepage_sections', sections)}
              onReset={handleResetHomepageSections}
              saving={settingsSaving}
              saved={settingsSaved === 'homepage_sections'}
              categories={categories}
            />
          )}

          {/* Homepage Slider Tab */}
          {tab === 'slider' && siteSettings && (
            <SliderManagerTab
              config={siteSettings.slider || { enabled: false, autoplay: true, interval_ms: 5000, article_ids: [] }}
              onSave={(config) => saveSiteSettings('slider', config)}
              saving={settingsSaving}
              saved={settingsSaved === 'slider'}
            />
          )}

          {/* Header & Navigation Tab */}
          {tab === 'header' && siteSettings && (
            <HeaderSettingsTab
              config={siteSettings.header || {}}
              onSave={(config) => saveSiteSettings('header', config)}
              onReset={handleResetHeaderMenu}
              saving={settingsSaving}
              saved={settingsSaved === 'header'}
            />
          )}

          {/* Breaking News Tab */}
          {tab === 'breaking' && siteSettings && (
            <BreakingNewsTab
              config={siteSettings.breaking_news || {}}
              onSave={(config) => saveSiteSettings('breaking_news', config)}
              saving={settingsSaving}
              saved={settingsSaved === 'breaking_news'}
            />
          )}

          {/* Cricket Cards Tab */}
          {tab === 'cricket' && siteSettings && (
            <CricketCardsTab
              initial={siteSettings.cricket_manual_matches || []}
              onSave={async (matches) => {
                setSettingsSaving(true);
                try {
                  await updateCricketManualMatches(matches);
                  setSiteSettings(s => ({ ...s, cricket_manual_matches: matches }));
                  setSettingsSaved('cricket_manual_matches');
                  setTimeout(() => setSettingsSaved(''), 2000);
                } catch (err) {
                  alert('Save failed: ' + (err?.response?.data?.detail || err?.message || ''));
                } finally { setSettingsSaving(false); }
              }}
              saving={settingsSaving}
              saved={settingsSaved === 'cricket_manual_matches'}
            />
          )}

          {/* Election Panel Tab */}
          {tab === 'election' && siteSettings && (
            <ElectionPanelTab
              config={siteSettings.election_panel || { enabled: false, parties: [] }}
              onSave={(config) => saveSiteSettings('election_panel', config)}
              saving={settingsSaving}
              saved={settingsSaved === 'election_panel'}
            />
          )}

          {/* Weather Widget Tab */}
          {tab === 'weather' && siteSettings && (
            <WeatherSettingsTab
              config={siteSettings.weather || {}}
              onSave={(config) => saveSiteSettings('weather', config)}
              saving={settingsSaving}
              saved={settingsSaved === 'weather'}
            />
          )}

          {/* Newsletter Tab */}
          {tab === 'newsletter' && <NewsletterTab />}

          {/* Media Library Tab */}
          {tab === 'media' && <MediaLibraryTab />}

          {/* General Settings Tab - Global Design + Comments + Ads + Hero */}
          {tab === 'settings' && siteSettings && (
            <div className="space-y-6">
              {/* Global Design Settings */}
              <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Global Design Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Font Family</label>
                    <select value={siteSettings.global_design?.font_family || 'Baloo Tamma 2'} onChange={e => saveSiteSettings('global_design', {...(siteSettings.global_design || {}), font_family: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[#EAEAE8] text-sm">
                      <option value="Baloo Tamma 2">Baloo Tamma 2</option>
                      <option value="Noto Sans Kannada">Noto Sans Kannada</option>
                      <option value="Aneka Kannada">Aneka Kannada</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Body Font Size</label>
                    <select value={siteSettings.global_design?.body_size || '14px'} onChange={e => saveSiteSettings('global_design', {...(siteSettings.global_design || {}), body_size: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[#EAEAE8] text-sm">
                      {['12px','13px','14px','15px','16px','17px','18px'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Summary Font Size</label>
                    <select value={siteSettings.global_design?.summary_size || '16px'} onChange={e => saveSiteSettings('global_design', {...(siteSettings.global_design || {}), summary_size: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[#EAEAE8] text-sm">
                      {['14px','15px','16px','17px','18px','20px'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Line Height</label>
                    <select value={siteSettings.global_design?.line_height || '1.9'} onChange={e => saveSiteSettings('global_design', {...(siteSettings.global_design || {}), line_height: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[#EAEAE8] text-sm">
                      {['1.4','1.5','1.6','1.7','1.8','1.9','2.0','2.2'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Body Font Weight</label>
                    <select value={siteSettings.global_design?.body_weight || '500'} onChange={e => saveSiteSettings('global_design', {...(siteSettings.global_design || {}), body_weight: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[#EAEAE8] text-sm">
                      {['400','500','600','700'].map(s => <option key={s} value={s}>{s} ({s==='400'?'Regular':s==='500'?'Medium':s==='600'?'Semibold':'Bold'})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Menu Font Size</label>
                    <select value={siteSettings.global_design?.menu_size || '16px'} onChange={e => saveSiteSettings('global_design', {...(siteSettings.global_design || {}), menu_size: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[#EAEAE8] text-sm">
                      {['13px','14px','15px','16px','18px'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Comments Control */}
              <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Comments</h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={siteSettings.comments_enabled === true} onChange={e => saveSiteSettings('comments_enabled', e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                  <div>
                    <span className="text-sm font-bold text-[#1A1A1A]">Enable Comments</span>
                    <p className="text-xs text-gray-500">When enabled, users can comment on articles</p>
                  </div>
                </label>
              </div>

              {/* Hero Banner Control */}
              <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Hero Banner Article</h3>
                <p className="text-sm text-gray-500 mb-3">Select which article appears as the main hero on the homepage. Leave empty for the latest published article.</p>
                <Input value={siteSettings.hero_article_id || ''} onChange={e => saveSiteSettings('hero_article_id', e.target.value || null)} placeholder="Article ID (e.g. art_abc123) or leave empty for latest" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" />
              </div>

              {/* Ad Management */}
              <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Advertisements</h3>
                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input type="checkbox" checked={siteSettings.ads?.enabled === true} onChange={e => saveSiteSettings('ads', {...(siteSettings.ads || {}), enabled: e.target.checked})} className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                  <span className="text-sm font-bold text-[#1A1A1A]">Enable Ads</span>
                </label>
                <p className="text-xs text-gray-500">Ad placements will be shown on homepage and article pages when enabled. Add banner URLs in the placements below.</p>
                <div className="mt-3 space-y-2">
                  {(siteSettings.ads?.placements || []).map((ad, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input value={ad.url || ''} onChange={e => { const p = [...(siteSettings.ads?.placements || [])]; p[idx] = {...p[idx], url: e.target.value}; saveSiteSettings('ads', {...(siteSettings.ads || {}), placements: p}); }} placeholder="Banner image URL" className="flex-1 bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" />
                      <select value={ad.position || 'homepage'} onChange={e => { const p = [...(siteSettings.ads?.placements || [])]; p[idx] = {...p[idx], position: e.target.value}; saveSiteSettings('ads', {...(siteSettings.ads || {}), placements: p}); }} className="h-10 px-3 rounded-lg border border-[#EAEAE8] text-sm">
                        <option value="homepage">Homepage</option>
                        <option value="article">Article Page</option>
                        <option value="sidebar">Sidebar</option>
                      </select>
                      <button onClick={() => { const p = (siteSettings.ads?.placements || []).filter((_, i) => i !== idx); saveSiteSettings('ads', {...(siteSettings.ads || {}), placements: p}); }} className="text-red-600 hover:text-red-700 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button onClick={() => saveSiteSettings('ads', {...(siteSettings.ads || {}), placements: [...(siteSettings.ads?.placements || []), {url: '', position: 'homepage', link: ''}]})} variant="outline" size="sm" className="mt-2">
                    <Plus className="w-4 h-4 mr-1" /> Add Placement
                  </Button>
                </div>
              </div>

              {/* Fix Broken Images */}
              <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Fix Broken Images</h3>
                <p className="text-sm text-gray-500 mb-3">Recovers images from persistent storage and clears dead URLs from the old deployment. Images stored via object storage will be restored.</p>
                <Button onClick={async () => {
                  if (!window.confirm('Fix all broken image URLs? Images from object storage will be recovered.')) return;
                  try {
                    const res = await fixBrokenImages({ old_host: 'news-redesign-phase3.emergent.host' });
                    alert(res.data.message);
                    window.location.reload();
                  } catch (e) { alert('Error: ' + e.message); }
                }} className="bg-green-600 hover:bg-green-700 text-white">
                  Recover & Fix Images
                </Button>
              </div>

              {/* Cleanup Test Data */}
              <div className="bg-white border border-[#EAEAE8] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Cleanup Test/Seed Data</h3>
                <p className="text-sm text-gray-500 mb-3">Remove all auto-generated test articles and fake subscribers. Only real articles will remain.</p>
                <Button onClick={async () => {
                  if (!window.confirm('Remove all test/seed data? This cannot be undone. Only real articles will remain.')) return;
                  try {
                    const res = await cleanupTestData();
                    alert(`Cleanup complete: ${res.data.articles_removed} test articles, ${res.data.test_subscribers_removed} test subscribers removed`);
                    window.location.reload();
                  } catch (e) { alert('Error: ' + e.message); }
                }} className="bg-red-700 hover:bg-red-800 text-white">
                  Remove Test Data
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
